import { HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  GameTypeEnum,
  type Game,
  type GameQueryParams,
  type GameResponse,
} from '@fantaz/shared/data-access/models';
import { map, Observable, of, tap } from 'rxjs';
import { FZ_CACHE_TTL } from '../../../interceptors';
import { ApiHttpService } from '../../../utils/api-http.service';
import {
  GameService,
  type FetchGameDetailsInput,
} from '../game-service.interface';

@Injectable({
  providedIn: 'root',
})
export class HttpApiGameService implements GameService {
  _httpClient = inject(ApiHttpService);

  fetchGameList(options?: GameQueryParams): Observable<GameResponse> {
    const endpoint = `/api/public/event-client/games-enabled-list`;
    let params = new HttpParams()
      .set('gameType', options?.gameType || GameTypeEnum.CASUAL)
      .set('pageIndex', options?.pageIndex || '0')
      .set('pageSize', options?.pageSize || '50');

    if (options?.gameTags?.length) {
      const tags = options.gameTags?.join(',');
      params = params.append('gameTags', tags);
    }

    if (options?.search) {
      params = params.append('search', options.search);
    }

    return this._httpClient
      .get<GameResponse>(endpoint, {
        params,
        context: new HttpContext().set(FZ_CACHE_TTL, 60000),
      })
      .pipe(
        tap((response) => {
          /*
           * Store into the local storage the game details
           */
          response.items.forEach((game) => {
            localStorage.setItem(
              `game-details-${game.key}`,
              JSON.stringify(game),
            );
          });
        }),
        map((response) => ({
          ...response,
          hasMore:
            response.total > (response.pageIndex + 1) * response.pageSize,
        })),
      );
  }

  fetchGameDetails(options: FetchGameDetailsInput): Observable<Game> {
    /*
     * In case of the game details are already stored in the local storage, we can return it directly
     */
    try {
      const str =
        localStorage.getItem(`game-details-${options.gameKey}`) || '{}';
      const parsed = JSON.parse(str);
      if (parsed.name) {
        return of(parsed);
      }
    } catch (error) {}

    return this.fetchGameList().pipe(
      map((response) => {
        return response.items.find(
          (game) => game.key === options.gameKey,
        ) as Game;
      }),
    );
  }
}
